// Content for the Personal AI Coaching purchase page (EN + FI).
// Structure is shared so both languages render with the same layout.
//
// TESTIMONIALS: only publish quotes with the customer's permission.
// Add or edit items in `testimonials` below; leave the array empty to hide the section.
// SESSION LOCATION: edit `faq` item "location" to change remote/in-person wording.

export type CoachingLocale = 'en' | 'fi';

export const SCHEDULING_URL = 'https://valto.youcanbookme.com';

export const coachingOptions = [
  { id: 'focused', priceId: 'coaching_focused_start_eur', hours: 3.5, price: 990 },
  { id: 'intensive', priceId: 'coaching_intensive_start_eur', hours: 7, price: 1750, recommended: true },
] as const;

export const coachingPaths = {
  en: { page: '/personal-ai-coaching', thanks: '/personal-ai-coaching/thank-you' },
  fi: { page: '/fi/personal-ai-coaching', thanks: '/fi/personal-ai-coaching/kiitos' },
} as const;

const en = {
  seo: {
    title: 'Personal AI Coaching for Owner-Entrepreneurs | Valto Loikkanen',
    description:
      'Intensive, hands-on Personal AI Coaching for owner-entrepreneurs. Start with a 3.5h or 7h session and become independently AI-native faster.',
  },
  hero: {
    eyebrow: '1:1 Personal AI Coaching',
    title: 'Personal AI Coaching for Owner-Entrepreneurs',
    subtitle: 'You won’t study AI separately from your work. We do your real work together, AI-natively.',
    cta: 'Choose your start',
  },
  vat: '+ VAT',
  options: {
    heading: 'Choose your start',
    lead: 'Two ways to begin. Both start from where you are today.',
    recommended: 'Recommended',
    focused: {
      title: 'Focused Start',
      duration: '3.5 hours',
      priceLabel: '€990',
      description:
        'A concentrated first session for getting your setup in place, identifying the highest-value next steps, and starting hands-on work on the things that matter most to you.',
      bestFor: [
        'getting started quickly',
        'clarifying your current AI maturity',
        'setting up essential tools and accounts',
        'choosing the first meaningful use case',
        'working through practical questions together',
      ],
      cta: 'Start your AI-native transition',
      ctaSub: '3.5h Focused Start, €990 + VAT',
    },
    intensive: {
      title: 'Intensive Start',
      duration: '7 hours · 2 × 3.5h sessions',
      priceLabel: '€1,750',
      description:
        'The recommended way to start when you want to move fast. Delivered as two 3.5-hour sessions: in the first we set up your working environment and start on real problems, between sessions you try the new way of working in your own work, and in the second we continue from where you got to.',
      bestFor: [
        'a serious first step toward AI-native working',
        'setting up your personal AI working environment',
        'working through multiple real use cases',
        'building or improving something concrete',
        'trying the new way of working in your own work between sessions',
      ],
      cta: 'Start your AI-native transition',
      ctaSub: '7h Intensive Start, €1,750 + VAT',
    },
    bestForLabel: 'Best for',
    after: {
      heading: 'Continue only as needed',
      body: 'After the initial session, additional coaching is available at',
      rate: '€250/hour + VAT',
      continueNote: 'Continue only as long as it is genuinely useful to you. No long-term package or monthly contract.',
      points: [
        'No package commitment.',
        'Book more time only when you feel it is useful.',
        'Sessions can be scheduled flexibly based on your needs and progress.',
      ],
    },
  },
  intro: {
    heading: 'What the coaching is',
    paragraphs: [
      'We sit on the same side of the table. You keep doing your own work as an entrepreneur. I help beside you to build an AI-native way of doing it.',
      'AI-native working is not mainly about learning new tools. It is about changing how you think, work, delegate, build, and make decisions.',
      'In Personal AI Coaching, we work directly on your own situation and current priorities. We set up what you need, solve real problems together, and build the confidence and capability for you to continue independently with your own agents.',
      'You leave with three things: the capability, your own AI working environment, and your first practical implementations — accounts, agents, tools, access, workflows and apps set up for real.',
    ],
    goalLabel: 'The goal is simple:',
    goal: 'My aim is not to build a permanent client relationship but to transfer the capability to you as quickly as possible. After that I am available ad hoc, whenever personal help is genuinely useful again.',
  },
  how: {
    heading: 'How it works',
    steps: [
      { title: 'Choose your starting session', body: 'Select 3.5h or 7h and pay securely online.' },
      { title: 'We agree the session timing', body: 'After purchase, you receive confirmation and instructions for scheduling.' },
      { title: 'We work hands-on', body: 'We start from your actual situation, priorities, questions, and projects.' },
      { title: 'Continue independently', body: 'My goal is to make myself unnecessary as quickly as possible. After that I am available ad hoc, when personal help genuinely adds value again.' },
    ],
  },
  topics: {
    heading: 'What we may work on',
    lead: 'There is no fixed curriculum. These are examples, not a syllabus.',
    items: [
      'personal AI setup and working environment',
      'working with AI by voice',
      'AI agents and delegation',
      'building small apps or internal tools',
      'AI-native website and customer experience',
      'personal and company information architecture',
      'automating workflows',
      'redesigning processes from first principles',
      'AI for research, planning, writing, analysis, and decision support',
      'deciding what should be done by humans, software, automation, or agents',
      'applying AI to your own business priorities',
    ],
    note: 'We do not need to start from a predefined exercise. If you already have something important on your desk, we can start from that.',
  },
  who: {
    heading: 'Designed for owner-entrepreneurs',
    body: 'This coaching is for people with meaningful ownership who actively lead their company. It is especially useful when you have people, customers, revenue and complex decisions to navigate — whether or not you already have technical people or an AI team. Company size is not the criterion.',
    reasons: [
      'you understand the whole business',
      'you can make decisions immediately',
      'you can connect AI directly to time, cost, growth, and opportunity',
      'you can lead changes in priorities across your organization',
      'once you understand what is possible, you can redesign how your company works',
    ],
    keyLine: 'AI can be outsourced. AI leadership cannot.',
  },
  not: {
    heading: 'What this is — and is not',
    notLabel: 'This is not',
    isLabel: 'This is',
    notItems: [
      'a generic AI course',
      'a slide-based training day',
      'a fixed 10-session program',
      'outsourced AI implementation',
      'a long-term consulting dependency',
    ],
    isItems: [
      'personal',
      'intensive',
      'practical',
      'adaptive',
      'entrepreneur-to-entrepreneur',
      'focused on building your own capability',
    ],
  },
  about: {
    heading: 'About Valto',
    paragraphs: [
      'I’m Valto Loikkanen, a long-time serial entrepreneur and advisor with experience across technology, digital business, startups, AI, and company development.',
      'I work entrepreneur-to-entrepreneur and focus on practical capability transfer rather than long-term dependency.',
      'I want you to become capable enough that you can continue with your own agents without me.',
    ],
    more: 'More about me',
    moreHref: '/about',
  },
  testimonials: {
    heading: 'In their words',
    items: [
      { quote: 'This is the best advice I have ever received in my life.', who: 'Owner-entrepreneur, after coaching sessions' },
    ] as { quote: string; who: string }[],
  },
  faq: {
    heading: 'Frequently asked questions',
    items: [
      { q: 'Do I need prior AI experience?', a: 'No. The session starts from your current level.' },
      { q: 'Do I need to prepare something?', a: 'No extensive preparation is required. It helps if you have access to the accounts, systems, and projects you want to work with.' },
      { q: 'Do we follow a fixed curriculum?', a: 'No. The session adapts to your situation and priorities.' },
      { q: 'Can we work on my actual business systems and projects?', a: 'Yes. That is often the best way to learn.' },
      { q: 'Is the session remote or in person?', a: 'Remote, in person in Helsinki, or as separately agreed.' },
      { q: 'What happens after purchase?', a: 'You receive a confirmation email and instructions for booking the session.' },
      { q: 'Can I buy more hours afterwards?', a: 'Yes. Additional coaching is €250/h + VAT.' },
      { q: 'Is VAT added?', a: 'Yes. All prices are shown + VAT, which is added at checkout.' },
    ],
  },
  final: {
    heading: 'Ready to become AI-native?',
    focusedCta: 'Start with 3.5h – €990 + VAT',
    intensiveCta: 'Start with 7h – €1,750 + VAT',
    secondary: 'Continue afterwards only if and when you need it.',
  },
  contact: {
    heading: 'Something specific to your company?',
    lead: 'General questions are best asked in the AI-Native Entrepreneurs community, where I answer openly and everyone benefits. If it is private or specific to your company, send me a message about a specific engagement.',
    direct: 'Prefer direct contact? Call or text +358 400 421551.',
    linkText: 'Something specific to your company? Send me a message',
  },
  checkout: {
    title: 'Secure checkout',
    close: 'Back to options',
    error: 'Checkout could not be opened. Please try again, or email valto@valtoai.com.',
  },
  thanks: {
    seoTitle: 'You’re booked — Personal AI Coaching | Valto AI',
    heading: 'You’re booked.',
    body: 'Thanks. The next step is to schedule your session and give me a little context so we can use the time well.',
    emailNote: 'A payment confirmation and receipt are on their way to your email.',
    schedule: 'Book your free 30-min preparation call',
    contactLead: 'Questions or want to reach me directly?',
    contactEmail: 'valto@valtoai.com',
    contactPhone: '+358 400 421551',
    contactPhoneNote: 'Call or text',
    formHeading: 'A little context',
    formLead: 'Optional, but it helps us use the time well. Takes about two minutes.',
    fields: {
      name: 'Name',
      email: 'Email',
      achieve: 'What do you most want to achieve?',
      current: 'What are you currently doing with AI?',
      valuable: 'What would make this session especially valuable?',
      project: 'Is there a specific project, process, or problem you want to work on?',
      dates: 'Preferred session dates / times',
      mode: 'Remote or in person?',
      modeRemote: 'Remote',
      modeInPerson: 'In person (Helsinki)',
      modeEither: 'Either works',
    },
    submit: 'Send',
    sending: 'Sending…',
    sent: 'Thanks — got it. I’ll be in touch shortly.',
    error: 'Couldn’t send. Please try again or email valto@valtoai.com.',
    back: 'Back to valtoai.com',
    backHref: '/',
  },
};

export type CoachingContent = typeof en;

const fi: CoachingContent = {
  seo: {
    title: 'Personal AI Coaching omistajayrittäjille | Valto Loikkanen',
    description:
      'Intensiivinen, käytännönläheinen Personal AI Coaching omistajayrittäjille. Aloita 3,5 tai 7 tunnin sessiolla ja tule nopeammin itsenäisesti tekoälynatiiviksi.',
  },
  hero: {
    eyebrow: '1:1 Personal AI Coaching',
    title: 'Personal AI Coaching omistajayrittäjille',
    subtitle: 'Et tule opiskelemaan tekoälyä irrallaan työstäsi. Teemme sinun oikeita töitäsi yhdessä AI-natiivisti.',
    cta: 'Valitse aloitus',
  },
  vat: '+ alv',
  options: {
    heading: 'Valitse aloitus',
    lead: 'Kaksi tapaa aloittaa. Molemmat lähtevät siitä, missä olet tänään.',
    recommended: 'Suositeltu',
    focused: {
      title: 'Fokusoitu aloitus',
      duration: '3,5 tuntia',
      priceLabel: '990 €',
      description:
        'Tiivis ensimmäinen sessio: saadaan perusasetukset kuntoon, tunnistetaan arvokkaimmat seuraavat askeleet ja aloitetaan käytännön työ sinulle tärkeimpien asioiden parissa.',
      bestFor: [
        'nopea liikkeellelähtö',
        'nykyisen tekoälytason selkeyttäminen',
        'keskeisten työkalujen ja tilien käyttöönotto',
        'ensimmäisen merkityksellisen käyttökohteen valinta',
        'käytännön kysymysten läpikäynti yhdessä',
      ],
      cta: 'Aloita siirtymä AI-natiiviksi',
      ctaSub: '3,5 h fokusoitu aloitus, 990 € + alv',
    },
    intensive: {
      title: 'Intensiivinen aloitus',
      duration: '7 tuntia · 2 × 3,5 h sessiota',
      priceLabel: '1 750 €',
      description:
        'Suositeltu tapa aloittaa, kun haluat edetä nopeasti. Toteutetaan kahtena 3,5 tunnin sessiona: ensimmäisessä pystytetään työympäristö ja aloitetaan oikeiden ongelmien ratkaisu, välissä kokeilet uutta työtapaa omassa työssäsi, ja toisessa jatketaan siitä, mihin pääsit.',
      bestFor: [
        'vakava ensiaskel tekoälynatiiviin työtapaan',
        'oman tekoälytyöympäristön rakentaminen',
        'useiden todellisten käyttökohteiden läpikäynti',
        'jonkin konkreettisen rakentaminen tai parantaminen',
        'uuden työtavan kokeileminen omassa työssä sessioiden välissä',
      ],
      cta: 'Aloita siirtymä AI-natiiviksi',
      ctaSub: '7 h intensiivinen aloitus, 1 750 € + alv',
    },
    bestForLabel: 'Sopii erityisesti',
    after: {
      heading: 'Jatka vain tarpeen mukaan',
      body: 'Aloitussession jälkeen lisävalmennusta on saatavilla hintaan',
      rate: '250 €/tunti + alv',
      continueNote: 'Jatkat vain niin kauan kuin siitä on sinulle aidosti hyötyä. Ei pitkäaikaista pakettia tai kuukausisopimusta.',
      points: [
        'Ei pakettisitoumusta.',
        'Varaa lisää aikaa vain, kun koet siitä olevan hyötyä.',
        'Sessiot sovitaan joustavasti tarpeidesi ja etenemisesi mukaan.',
      ],
    },
  },
  intro: {
    heading: 'Mitä valmennus on',
    paragraphs: [
      'Istumme samalla puolella pöytää. Sinä jatkat omien yrittäjän töidesi tekemistä. Minä autan vieressä rakentamaan siihen AI-natiivin tavan toimia.',
      'Tekoälynatiivi työskentely ei ole ensisijaisesti uusien työkalujen opettelua. Se on muutos siinä, miten ajattelet, työskentelet, delegoit, rakennat ja teet päätöksiä.',
      'Personal AI Coachingissa työskentelemme suoraan sinun tilanteesi ja ajankohtaisten prioriteettiesi parissa. Pystytämme tarvittavan, ratkaisemme todellisia ongelmia yhdessä ja rakennamme varmuuden ja kyvykkyyden, jotta voit jatkaa itsenäisesti omien agenttiesi kanssa.',
      'Saat kolme asiaa: osaamisen, oman työskentely-ympäristön ja ensimmäiset käytännön toteutukset — tilit, agentit, työkalut, käyttöoikeudet, työnkulut ja sovellukset oikeasti käyttöön.',
    ],
    goalLabel: 'Tavoite on yksinkertainen:',
    goal: 'Tavoitteeni ei ole rakentaa pysyvää asiakassuhdetta vaan siirtää osaaminen sinulle mahdollisimman nopeasti. Sen jälkeen olen tarvittaessa käytettävissä ad hoc, kun tulee jotain missä henkilökohtaisesta avusta on taas aidosti hyötyä.',
  },
  how: {
    heading: 'Miten se toimii',
    steps: [
      { title: 'Valitse aloitussessio', body: 'Valitse 3,5 h tai 7 h ja maksa turvallisesti verkossa.' },
      { title: 'Sovimme ajankohdan', body: 'Oston jälkeen saat vahvistuksen ja ohjeet ajan varaamiseen.' },
      { title: 'Teemme käytännössä', body: 'Lähdemme liikkeelle todellisesta tilanteestasi, prioriteeteistasi, kysymyksistäsi ja projekteistasi.' },
      { title: 'Jatkat itsenäisesti', body: 'Tavoitteeni on tehdä itseni tarpeettomaksi mahdollisimman nopeasti. Sen jälkeen olen käytettävissä ad hoc, kun henkilökohtaisesta avusta on taas aidosti hyötyä.' },
    ],
  },
  topics: {
    heading: 'Mitä voimme tehdä',
    lead: 'Kiinteää opetussuunnitelmaa ei ole. Nämä ovat esimerkkejä, eivät ohjelma.',
    items: [
      'oma tekoälyasetus ja työympäristö',
      'tekoälyn kanssa työskentely puheella',
      'tekoälyagentit ja delegointi',
      'pienten sovellusten tai sisäisten työkalujen rakentaminen',
      'tekoälynatiivi verkkosivusto ja asiakaskokemus',
      'henkilökohtainen ja yrityksen tietoarkkitehtuuri',
      'työnkulkujen automatisointi',
      'prosessien uudelleensuunnittelu perusteista lähtien',
      'tekoäly tiedonhakuun, suunnitteluun, kirjoittamiseen, analyysiin ja päätöksenteon tukeen',
      'mitä tekevät ihmiset, ohjelmistot, automaatio tai agentit',
      'tekoälyn soveltaminen omiin liiketoimintaprioriteetteihisi',
    ],
    note: 'Meidän ei tarvitse aloittaa valmiista harjoituksesta. Jos pöydälläsi on jo jotain tärkeää, voimme aloittaa siitä.',
  },
  who: {
    heading: 'Suunniteltu omistajayrittäjille',
    body: 'Valmennus on ihmisille, joilla on merkittävää omistusta ja jotka johtavat yritystään aktiivisesti. Siitä on erityistä hyötyä, kun johdat ihmisiä, asiakkaita ja toimivaa liiketoimintaa sekä teet monimutkaisia päätöksiä — oli sinulla teknologiaosaajia tai AI-tiimi jo valmiiksi tai ei. Yrityksen koko ei ratkaise.',
    reasons: [
      'ymmärrät koko liiketoiminnan',
      'voit tehdä päätöksiä heti',
      'voit kytkeä tekoälyn suoraan aikaan, kustannuksiin, kasvuun ja mahdollisuuksiin',
      'voit johtaa prioriteettien muutosta organisaatiossasi',
      'kun ymmärrät, mikä on mahdollista, voit suunnitella uudelleen, miten yrityksesi toimii',
    ],
    keyLine: 'Tekoälyn voi ulkoistaa. Tekoälyjohtajuutta ei voi.',
  },
  not: {
    heading: 'Mitä tämä on — ja mitä ei',
    notLabel: 'Tämä ei ole',
    isLabel: 'Tämä on',
    notItems: [
      'yleinen tekoälykurssi',
      'diapohjainen koulutuspäivä',
      'kiinteä 10 session ohjelma',
      'ulkoistettu tekoälytoteutus',
      'pitkäaikainen konsulttiriippuvuus',
    ],
    isItems: [
      'henkilökohtainen',
      'intensiivinen',
      'käytännönläheinen',
      'mukautuva',
      'yrittäjältä yrittäjälle',
      'keskittyy oman kyvykkyytesi rakentamiseen',
    ],
  },
  about: {
    heading: 'Valtosta',
    paragraphs: [
      'Olen Valto Loikkanen, pitkän linjan sarjayrittäjä ja neuvonantaja. Kokemusta on teknologiasta, digitaalisesta liiketoiminnasta, startupeista, tekoälystä ja yritysten kehittämisestä.',
      'Työskentelen yrittäjältä yrittäjälle ja keskityn käytännön osaamisen siirtoon pitkäaikaisen riippuvuuden sijaan.',
      'Haluan, että sinusta tulee niin kyvykäs, että voit jatkaa omien agenttiesi kanssa ilman minua.',
    ],
    more: 'Lisää minusta',
    moreHref: '/fi/tietoa',
  },
  testimonials: {
    heading: 'Asiakkaiden sanoin',
    items: [
      { quote: 'Tämä on kyllä elämäni parasta neuvontaa mitä olen koskaan saanut.', who: 'Omistajayrittäjä valmennussessioiden jälkeen' },
    ],
  },
  faq: {
    heading: 'Usein kysyttyä',
    items: [
      { q: 'Tarvitsenko aiempaa tekoälykokemusta?', a: 'Et. Sessio lähtee nykyiseltä tasoltasi.' },
      { q: 'Pitääkö minun valmistautua jotenkin?', a: 'Laajaa valmistautumista ei tarvita. On hyödyllistä, jos sinulla on pääsy niihin tileihin, järjestelmiin ja projekteihin, joiden parissa haluat työskennellä.' },
      { q: 'Seuraammeko kiinteää ohjelmaa?', a: 'Emme. Sessio mukautuu tilanteeseesi ja prioriteetteihisi.' },
      { q: 'Voimmeko työskennellä yritykseni todellisten järjestelmien ja projektien parissa?', a: 'Kyllä. Se on usein paras tapa oppia.' },
      { q: 'Onko sessio etänä vai paikan päällä?', a: 'Etänä, paikan päällä Helsingissä tai erikseen sovitusti.' },
      { q: 'Mitä oston jälkeen tapahtuu?', a: 'Saat vahvistuksen sähköpostiin sekä ohjeet session varaamiseen.' },
      { q: 'Voinko ostaa lisää tunteja myöhemmin?', a: 'Kyllä. Lisävalmennus on 250 €/h + alv.' },
      { q: 'Lisätäänkö hintoihin alv?', a: 'Kyllä. Kaikki hinnat ovat + alv, ja vero lisätään kassalla.' },
    ],
  },
  final: {
    heading: 'Valmis tulemaan tekoälynatiiviksi?',
    focusedCta: 'Aloita 3,5 h – 990 € + alv',
    intensiveCta: 'Aloita 7 h – 1 750 € + alv',
    secondary: 'Jatka myöhemmin vain, jos ja kun tarvitset.',
  },
  contact: {
    heading: 'Onko asia juuri sinun yrityksesi?',
    lead: 'Yleiset kysymykset kannattaa kysyä AI-natiivit yrittäjät -yhteisössä, jossa vastaan avoimesti ja kaikki hyötyvät. Jos asia on henkilökohtainen tai koskee juuri sinun yritystäsi, lähetä viesti tietystä yhteistyöstä.',
    direct: 'Suora yhteys: soita tai laita viesti +358 400 421551.',
    linkText: 'Koskeeko asia juuri sinun yritystäsi? Lähetä viesti',
  },
  checkout: {
    title: 'Turvallinen maksu',
    close: 'Takaisin vaihtoehtoihin',
    error: 'Maksua ei voitu avata. Yritä uudelleen tai lähetä sähköpostia osoitteeseen valto@valtoai.com.',
  },
  thanks: {
    seoTitle: 'Varaus vahvistettu — Personal AI Coaching | Valto AI',
    heading: 'Varauksesi on vahvistettu.',
    body: 'Kiitos. Seuraavaksi varataan sessiolle aika, ja voit kertoa vähän taustaa, jotta saamme ajasta parhaan hyödyn.',
    emailNote: 'Maksuvahvistus ja kuitti ovat tulossa sähköpostiisi.',
    schedule: 'Varaa ilmainen 30 min valmistelupuhelu',
    contactLead: 'Kysymyksiä tai haluat tavoittaa minut suoraan?',
    contactEmail: 'valto@valtoai.com',
    contactPhone: '+358 400 421551',
    contactPhoneNote: 'Soita tai laita viesti',
    formHeading: 'Vähän taustaa',
    formLead: 'Vapaaehtoinen, mutta auttaa käyttämään ajan hyvin. Vie noin kaksi minuuttia.',
    fields: {
      name: 'Nimi',
      email: 'Sähköposti',
      achieve: 'Mitä haluat eniten saavuttaa?',
      current: 'Mitä teet tällä hetkellä tekoälyn kanssa?',
      valuable: 'Mikä tekisi sessiosta erityisen arvokkaan?',
      project: 'Onko jokin tietty projekti, prosessi tai ongelma, jonka parissa haluat työskennellä?',
      dates: 'Toivotut päivät / ajat',
      mode: 'Etänä vai paikan päällä?',
      modeRemote: 'Etänä',
      modeInPerson: 'Paikan päällä (Helsinki)',
      modeEither: 'Kumpi tahansa käy',
    },
    submit: 'Lähetä',
    sending: 'Lähetetään…',
    sent: 'Kiitos — tiedot vastaanotettu. Olen pian yhteydessä.',
    error: 'Lähetys epäonnistui. Yritä uudelleen tai lähetä sähköpostia osoitteeseen valto@valtoai.com.',
    back: 'Takaisin etusivulle',
    backHref: '/fi',
  },
};

export const coachingContent: Record<CoachingLocale, CoachingContent> = { en, fi };
